import React, { useEffect, useState } from "react";
import Layout from "../../layouts/Layout";
import {
  Backdrop,
  Box,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  Grid2,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  Check,
  Close,
  CreditCard,
  DescriptionOutlined,
  EmailOutlined,
  PersonOutline,
  UploadOutlined,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { colors } from "../../theme/theme";
import { SecondaryBtn } from "../../Components/Common/CustomBtn";
import { CustomInputLabel } from "../../Components/Creators/CustomInputLabel";
import {
  CustomInput,
  CustomTextArea,
} from "../../Components/Common/CustomInput";
import { CustomDropDown } from "../../Components/Common/CustomDropdown";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../Components/Common/CustomSnackbar";
import FileUpload from "../../Components/Common/FileUpload";
import CustomToggle from "../../Components/Common/CustomToggle";
import StripeModal from "../../Components/StripeModal/StripeModal";

import { userTypes } from "../../assets/data";
import { getUserById, UpdateUser } from "../../redux/actions/user.dispatcher";
import { getSubscriptionOfUser } from "../../redux/actions/subscriptions.dispatcher";
import axiosInstance from "../../utils/config/config";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const subscriptionOptions = [
  {
    id: 1,
    label: "Basic Plan - Only one tier",
    value: "Basic Plan",
    maxPackages: 1,
  },
  {
    id: 2,
    label: "Growth Plan - Up to 3 tiers",
    value: "Growth Plan",
    maxPackages: 3,
  },
];

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userData } = useSelector((state) => state.user);

  const mediumScreen = useMediaQuery("(max-width:960px)");
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [uploadUrl, setUploadUrl] = useState(userData?.avatar || null);
  const [errorState, setErrorState] = useState({ error: false, message: "" });
  const [showUpdatedBox, setShowUpdatedBox] = useState(false);

  const [firstName, setFirstName] = useState(userData?.firstName || "");
  const [lastName, setLastName] = useState(userData?.lastName || "");
  const [bio, setBio] = useState(userData?.bio || "");
  const [email, setEmail] = useState(userData?.email || "");
  const [bankName, setBankName] = useState(userData?.bankName || "");
  const [iban, setIban] = useState(userData?.iban || "");
  const [subscription, setSubscription] = useState(
    userData?.subscriptionPlan || ""
  );
  const [prevSub, setPrevSub] = useState(userData.subscriptionPlan || "");
  const [phone, setPhone] = useState(userData?.phone || "");
  const [instagram, setInstagram] = useState(userData?.instagram || "");
  const [twitterURL, settwitterURL] = useState(userData?.twitterUrl || "");
  const [youtubeUrl, setYoutubeUrl] = useState(userData?.youtube || "");
  const [mfaEnabled, setMfaEnabled] = useState(userData?.MfaEnabled || false);
  const [messagingEnabled, setMessagingEnabled] = useState(
    userData?.messagingEnabled || true
  );
  const [autoMessagingEnabled, setAutoMessagingEnabled] = useState(
    userData?.autoMessagingEnabled || false
  );
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tiktokUrl, settiktokUrl] = useState(userData?.tiktokUrl || "");
  const [userType, setUserType] = useState(userData?.userType || "");
  const [packages, setPackages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [welcomeMessage, setWelcomeMessage] = useState(
    userData.AutoMessage?.welcomeMessage || ""
  );
  const [paymentConfirmation, setPaymentConfirmation] = useState(
    userData.AutoMessage?.paymentConfirmation || ""
  );
  const [promoOffer, setPromoOffer] = useState(
    userData.AutoMessage?.promoOffer || ""
  );

  useEffect(() => {
    dispatch(getUserById(userData?.id));
    fetchUserSubscriptions();
  }, []);

  const handleEventFormChange = (value) => {
    setSubscription(value);
  };
  const fetchUserSubscriptions = async () => {
    try {
      const response = await getSubscriptionOfUser(userData?.creatorId);

      if (Array.isArray(response) && response.length > 0) {
        setPackages(
          response.map((pkg) => ({
            title: pkg.packageName || "",
            description: pkg.description || "",
            price: pkg.price || "",
            fixedDiscountOnPPV: pkg.fixedDiscountOnPPV || false,
            discount: pkg.discount || null,
            accessMessaging: pkg.accessMessaging || false,
            accessContent: pkg.accessContent || false,
            accessDynamicCalender: pkg.accessDynamicCalender || false,
          }))
        );
      }
    } catch (err) {
      console.log("Error fetching subscriptions:", err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const update = await UpdateUser(
        {
          userId: userData.id,
          email,
          firstName,
          lastName,
          instagram,
          bankName,
          iban,
          youtubeUrl,
          tiktokUrl,
          twitterUrl: twitterURL,
          bio,
          phone,
          avatar: uploadUrl,
          mfaEnabled,
          messagingEnabled,
          autoMessagingEnabled,
        },
        {
          welcomeMessage,
          promoOffer,
          paymentConfirmation,
        }
      );

      if (update && userData.creatorId) {
        await axiosInstance.put(`/subscription/updateSubscriptionPackages`, {
          creator: userData?.creatorId,
          packages,
        });

        if (prevSub !== subscription) {
          const planMeta = subscriptionOptions.find(
            (p) => p.value === subscription
          );

          if (smallScreen) {
            navigate("/payment-page", {
              state: {
                selectedPlan: planMeta,
              },
            });
          } else {
            setSelectedPlan(planMeta);
            setIsPaymentModalOpen(true);
            return; // exit to show modal first
          }
        }

        setSuccess(true);
        dispatch(getUserById(userData.id));
        setShowUpdatedBox(true);
      }
    } catch (error) {
      console.error("❌ Failed to update user", error);
    } finally {
      setLoading(false);
    }
  };

  const subscriptionOptions = [
    {
      id: 1,
      label: "Basic plan - Only one tier",
      value: "Basic Plan",
      maxPackages: 1,
      price: 500, // $5.00
      percentage: "6",
    },
    {
      id: 2,
      label: "Growth plan - Up to 3 tiers",
      value: "Growth Plan",
      maxPackages: 3,
      price: 1500, // $15.00
      percentage: "9",
    },
  ];

  useEffect(() => {
    if (showUpdatedBox) {
      setTimeout(() => {
        setShowUpdatedBox(false);
      }, 4000);
    }
  }, [showUpdatedBox]);

  const handlePackageChange = (index, field, value) => {
    setPackages((prevPackages) => {
      const updatedPackages = [...prevPackages];
      updatedPackages[index][field] = value;
      return updatedPackages;
    });
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(
        "/payments/unsubscribeOnlyhands",
        {
          userId: userData.id,
        }
      );

      if (data.success) {
        dispatch(getUserById(userData.id));
      } else {
        console.log("Unsubscription Failed", data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const selectedPlan = subscriptionOptions.find(
      (plan) => plan.value === subscription
    );
    const maxPackages = selectedPlan ? selectedPlan.maxPackages : 0;

    setPackages((prevPackages) => {
      // ✅ If API already loaded packages, keep them but ensure correct length
      let updatedPackages = [...prevPackages];

      // ✅ Reduce packages if more than allowed
      if (updatedPackages.length > maxPackages) {
        updatedPackages = updatedPackages.slice(0, maxPackages);
      }
      // ✅ Increase packages if fewer than allowed
      while (updatedPackages.length < maxPackages) {
        updatedPackages.push({ title: "", description: "", price: "" });
      }

      return updatedPackages;
    });
  }, [subscription]);

  useEffect(() => {
    if (!userData) return;

    setFirstName(userData.firstName || "");
    setLastName(userData.lastName || "");
    setEmail(userData.email || "");
    setBio(userData.bio || "");
    setPhone(userData.phone || "");
    setUploadUrl(userData.avatar || null);
    setInstagram(userData.instagram || "");
    setYoutubeUrl(userData.youtube || "");
    settwitterURL(userData.twitterUrl || "");
    settiktokUrl(userData.tiktokUrl || "");
    setBankName(userData.bankName || "");
    setIban(userData.iban || "");
    setSubscription(userData.subscriptionPlan || "");
    setPrevSub(userData.subscriptionPlan || "");
    setMfaEnabled(userData.MfaEnabled || false);
    setMessagingEnabled(userData.messagingEnabled || false);
    setAutoMessagingEnabled(userData.autoMessagingEnabled || false);
    setUserType(userData.userType || "");
  }, [userData]);

  return (
    <Elements stripe={stripePromise}>
      <Layout>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        {/* Header */}
        <ErrorSnackbar
          error={errorState.error}
          errorMsg={errorState.message}
          handleCloseError={() => setErrorState({ error: false, message: "" })}
        />
        <Stack
          direction={mediumScreen ? "column" : "row"}
          sx={{
            justifyContent: mediumScreen ? "center" : "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "32px",
              fontFamily: "Inter-Bold",
              color: colors.textColor,
            }}
          >
            Edit Profile
          </Typography>

          <Stack
            direction={"row"}
            sx={{
              gap: 1,
              alignItems: "center",
              width: mediumScreen ? "100%" : "50%",
              justifyContent: "flex-end",
              mt: mediumScreen ? 2 : 0,
            }}
          >
            <SecondaryBtn
              text={"Close"}
              Icon={Close}
              width={mediumScreen ? "35%" : "20%"}
              noMb
              noMt
              black
              onClick={() => navigate(-1)}
            />
            <SecondaryBtn
              text={"Save"}
              Icon={Check}
              width={mediumScreen ? "35%" : "20%"}
              noMb
              noMt
              filled
              onClick={handleSave}
            />
          </Stack>
        </Stack>

        {/* Form */}
        <Grid2 container spacing={2} sx={{ mt: 3, mb: 10 }} className="step-4">
          {showUpdatedBox && (
            <Grid2 item size={{ xs: 12 }}>
              <Box
                sx={{
                  width: "100%",
                  border: "1px solid green",
                  background: "#90ee90",
                  height: "10vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  px: 4,
                }}
              >
                <Typography>Profile Saved</Typography>
              </Box>
            </Grid2>
          )}
          <Grid2 item size={{ xs: 12, md: 2 }}>
            <CustomInputLabel text="Profile Picture" />
            <FileUpload
              fileType="image"
              setUploadUrl={setUploadUrl}
              uploadUrl={uploadUrl}
              setLoading={setLoading}
            />
          </Grid2>

          <Grid2 item size={{ xs: 12, md: 10 }} spacing={2}>
            <Grid2 container spacing={2}>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="First Name" />
                <CustomInput
                  value={firstName}
                  setValue={setFirstName}
                  Icon={PersonOutline}
                  placeholder="e.g. John"
                />
              </Grid2>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="Last Name" />
                <CustomInput
                  value={lastName}
                  setValue={setLastName}
                  Icon={PersonOutline}
                  placeholder="e.g. Doe"
                />
              </Grid2>
            </Grid2>

            <Grid2 container spacing={2}>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="Email" />
                <CustomInput
                  value={email}
                  setValue={setEmail}
                  Icon={EmailOutlined}
                  placeholder="e.g. john@gmail.com"
                />
              </Grid2>
              <Grid2 item size={{ xs: 12, md: 3 }}>
                <CustomInputLabel text="Enable MFA" />
                <CustomToggle
                  label={"Do you want to enable MFA?"}
                  checked={mfaEnabled}
                  onChange={() => {
                    setMfaEnabled(!mfaEnabled);
                  }}
                />
              </Grid2>
              <Grid2 item size={{ xs: 12, md: 3 }}>
                <CustomInputLabel text="Do you want messaging enabled?" />
                <CustomToggle
                  label={"Enable Messaging?"}
                  checked={messagingEnabled}
                  onChange={() => {
                    setMessagingEnabled(!messagingEnabled);
                  }}
                />
              </Grid2>
            </Grid2>
            {/* <Grid2 container spacing={2}>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Bank Name" />
              <CustomInput
                value={bankName}
                setValue={setBankName}
                Icon={CreditCard}
                placeholder="e.g. ABC Bank"
                mB={mediumScreen ? 0.1 : 2}
              />
            </Grid2>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="IBAN" />
              <CustomInput
                value={iban}
                setValue={setIban}
                Icon={AccountBalanceOutlined}
                placeholder="e.g. 1234567891234"
              />
            </Grid2>
          </Grid2> */}

            <Box sx={{ mt: 2 }}>
              <CustomInputLabel text="Bio" />
              <CustomTextArea
                Icon={DescriptionOutlined}
                value={bio}
                setValue={setBio}
                placeholder="e.g. I am a fitness enthusiast and a martial artist. I love to share my journey with you all."
              />
            </Box>

            <Grid2 container spacing={2}>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="Youtube" />
                <CustomInput
                  value={youtubeUrl}
                  setValue={setYoutubeUrl}
                  Icon={EmailOutlined}
                  placeholder="e.g. youtube.com/johnDoe"
                />
              </Grid2>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="X" />
                <CustomInput
                  value={twitterURL}
                  setValue={settwitterURL}
                  Icon={EmailOutlined}
                  placeholder="e.g. twitter.com/johnDoe"
                />
              </Grid2>
            </Grid2>
            <Grid2 container spacing={2}>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="Instagram" />
                <CustomInput
                  value={instagram}
                  setValue={setInstagram}
                  Icon={EmailOutlined}
                  placeholder="e.g. @jonDoe"
                />
              </Grid2>
              <Grid2 item size={{ xs: 12, md: 6 }}>
                <CustomInputLabel text="TikTok" />
                <CustomInput
                  value={tiktokUrl}
                  setValue={settiktokUrl}
                  Icon={EmailOutlined}
                  placeholder="e.g. tiktok.com/johnDoe"
                />
              </Grid2>
            </Grid2>
            {userData.creatorId && (
              <Grid2 container>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                  <CustomInputLabel text="Automated Messages" />
                  <CustomToggle
                    label={"Enable Automated Messaging?"}
                    checked={autoMessagingEnabled}
                    onChange={() => {
                      setAutoMessagingEnabled(!autoMessagingEnabled);
                    }}
                  />
                </Grid2>
              </Grid2>
            )}
            {userData.creatorId && autoMessagingEnabled && (
              <Grid2 container spacing={2} mt={2}>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                  <CustomInputLabel text="Automated Welcome Message" />
                  <CustomInput
                    value={welcomeMessage}
                    setValue={setWelcomeMessage}
                    Icon={EmailOutlined}
                    placeholder="Welcome to my channel"
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                  <CustomInputLabel text="Automated Payment Confirmation Message" />
                  <CustomInput
                    value={paymentConfirmation}
                    setValue={setPaymentConfirmation}
                    Icon={EmailOutlined}
                    placeholder="Payment Received"
                  />
                </Grid2>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                  <CustomInputLabel text="Automated Promo Offer Message" />
                  <CustomInput
                    value={promoOffer}
                    setValue={setPromoOffer}
                    Icon={EmailOutlined}
                    placeholder="e.g. new Offer"
                  />
                </Grid2>
              </Grid2>
            )}
            {userData.creatorId && (
              <Grid2 container spacing={2}>
                <Grid2 item size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                  <CustomInputLabel text="Subscription Plan" />
                  <CustomDropDown
                    value={subscription}
                    setValue={handleEventFormChange}
                    title="Subscription Plan"
                    data={subscriptionOptions}
                    noIcon
                    noMt
                  />
                </Grid2>
                {userData.subscribed && userData.subscriptionPlan && (
                  <Grid2 item size={{ xs: 12, md: 6 }}>
                    <CustomInputLabel
                      text={`Unsubscribe from ${userData.subscriptionPlan}`}
                    />
                    <SecondaryBtn
                      text={"Unsubscribe"}
                      Icon={Close}
                      width={"100%"}
                      noMb
                      noMt
                      black
                      onClick={handleUnsubscribe}
                    />
                  </Grid2>
                )}

                {packages.length > 0 && subscription && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{ fontSize: "20px", fontWeight: "bold", mb: 2 }}
                    >
                      Subscription Packages
                    </Typography>

                    {packages.map((pkg, index) => (
                      <Box key={index} sx={{ mt: 2 }}>
                        <Typography
                          sx={{ fontSize: "18px", fontWeight: "bold", mb: 1 }}
                        >
                          Package {index + 1}
                        </Typography>

                        <Grid2 container spacing={2}>
                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Title" />
                            <CustomInput
                              value={pkg.title}
                              setValue={(value) =>
                                handlePackageChange(index, "title", value)
                              }
                              noIcon
                              placeholder="Package Title"
                            />
                          </Grid2>

                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Price ($)" />
                            <CustomInput
                              value={pkg.price}
                              setValue={(value) =>
                                handlePackageChange(index, "price", value)
                              }
                              placeholder="e.g. 49.99"
                              noIcon
                              width="100%" // Ensures input takes full width
                            />
                          </Grid2>

                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Access To Messaging" />
                            <CustomToggle
                              label={"Give access to messaging?"}
                              checked={pkg.accessMessaging || false}
                              onChange={() => {
                                if (subscription === "Basic Plan") {
                                  setErrorState({
                                    error: true,
                                    message:
                                      "This feature is only available in Growth Plan",
                                  });
                                  return;
                                } else {
                                  handlePackageChange(
                                    index,
                                    "accessMessaging",
                                    !pkg.accessMessaging
                                  );
                                }
                              }}
                            />
                            {/* <CustomInputLabel text="Description" />
                          <CustomTextArea
                            value={pkg.description}
                            setValue={(value) =>
                              handlePackageChange(index, "description", value)
                            }
                            placeholder="Package Description"
                            noIcon
                            width="100%" // Ensures input takes full width
                          /> */}
                          </Grid2>
                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Access Content" />
                            <CustomToggle
                              label={"Give access to content?"}
                              checked={pkg.accessContent || false}
                              onChange={() => {
                                handlePackageChange(
                                  index,
                                  "accessContent",
                                  !pkg.accessContent
                                );
                              }}
                            />
                          </Grid2>
                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Access Dynamic Calender" />
                            <CustomToggle
                              label={"Give access to dynamic calender?"}
                              checked={pkg.accessDynamicCalender || false}
                              onChange={() => {
                                handlePackageChange(
                                  index,
                                  "accessDynamicCalender",
                                  !pkg.accessDynamicCalender
                                );
                              }}
                            />
                          </Grid2>
                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Fixed Discount on PPV" />
                            <CustomToggle
                              label={"Give discount on PPV?"}
                              checked={pkg.fixedDiscountOnPPV || false}
                              onChange={() => {
                                if (subscription == "Basic Plan") {
                                  setErrorState({
                                    error: true,
                                    message:
                                      "This feature is only available in Growth Plan",
                                  });
                                  return;
                                } else {
                                  handlePackageChange(
                                    index,
                                    "fixedDiscountOnPPV",
                                    !pkg.fixedDiscountOnPPV
                                  );
                                }
                              }}
                            />
                          </Grid2>
                          {pkg.fixedDiscountOnPPV && (
                            <Grid2 item size={{ xs: 12, md: 6 }}>
                              <CustomInputLabel text="Discount (%)" />
                              <CustomInput
                                value={pkg.discount || ""}
                                setValue={(value) =>
                                  handlePackageChange(index, "discount", value)
                                }
                                placeholder="e.g. 10"
                                noIcon
                                width="100%"
                              />
                            </Grid2>
                          )}
                          <Grid2 item size={{ xs: 12, md: 6 }}>
                            <CustomInputLabel text="Description" />
                            <CustomTextArea
                              value={pkg.description}
                              setValue={(value) =>
                                handlePackageChange(index, "description", value)
                              }
                              placeholder="Package Description"
                              noIcon
                              width="100%" // Ensures input takes full width
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid2>
            )}
          </Grid2>
        </Grid2>

        <StripeModal
          open={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          customerId={userData.stripeCustomerId}
          plan={subscription}
          onSubscriptionCreated={async () => {
            setIsPaymentModalOpen(false);
            setSuccess(true);
            setPrevSub(subscription);
            dispatch(getUserById(userData.id));
          }}
        />
        <ConfirmationSnackbar
          snackbarState={success}
          handleSnackbarClose={() => setSuccess(false)}
          text={"Profile updated successfully"}
        />
      </Layout>
    </Elements>
  );
};

export default EditProfile;
